// The embed's interaction layer for Valet orders: the package's `OrderInteraction` contract,
// served over the portal's shared dialogs.
//
// A transposition of the dealer's `ScenarioActionsProvider` — same seven verbs, same one-of-each
// mounting (a page of twenty-five cards mounts one photo viewer and one of each dialog), same
// on-open fetch for the subject move. All four dialogs are now the portal's, shared from
// `@hopdrive/feature-move-actions` rather than forked here — which is decisions C12 and C13
// settled. Navigation targets the `/moves` prefix and carries the embed's query string, which is
// where its presentation config rides.

import React, { createContext, useContext, useMemo, useState } from 'react';
import * as Sentry from '@sentry/react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  INERT_ORDER_INTERACTION,
  toOrderCardActions,
} from '@hopdrive/feature-order-index';
import {
  CancelLiteModal,
  MoveCancelModal,
  MoveTimeModal,
} from '@hopdrive/feature-move-actions';
import type { OrderInteraction, ValetOrderHandlers } from '@hopdrive/feature-order-index';
import type { ScenarioAssignmentAction, ScenarioView } from '@hopdrive/feature-order-index';

import { ScenarioPhotoLightboxByLegPhase, type ScenarioLegPhotoTarget } from '@components/ScenarioPhotoLightbox';
import AssignOrUnassignMoveModal from '@components/Modals/AssignOrUnassignMoveModal';
import { useChatModal } from '@components/Chat/ChatModalManager';
import { useGetMoveDetailsQuery } from '@features/moves/queries/getMoveDetails.query.generated';
import { findOffersInScenarios } from '@services/moveService';
import siteConfig from '@config';
import { useUser } from '@store';
import { actorIdentity } from '@utils/actorIdentity';
import { appendSearch } from '@utils/appendSearch';
import type { Moves, Offers } from '@gql/base-types';

import { useOrderAssignmentSettling } from './useOrderAssignmentSettling';
import { movePath, planMovesPath } from '@features/products/productRoutes';

type EmbedDialogKind = 'assign' | 'changeTime' | 'cancel';

interface EmbedDialogTarget {
  kind: EmbedDialogKind;
  moveId: number;
  scenarioId: number;
}

// Defaulting to the inert layer rather than null: a card rendered outside the provider (a
// preview, a story) comes out readable and actionless instead of throwing.
const EmbedOrderInteractionContext = createContext<OrderInteraction>(INERT_ORDER_INTERACTION);

/** The embed's `OrderInteraction`, supplied through `EmbedOrderIndexAdapter`. */
export const useEmbedOrderInteraction = (): OrderInteraction => useContext(EmbedOrderInteractionContext);

/**
 * The embed's guest-SMS chat, opened for one move. Null-safe: the chat context throws where its
 * provider is not mounted, and an order surface outside the app layout should lose the chat
 * action, not crash. Chat is a lite-tier surface, so this is null for everyone else too.
 */
const useEmbedGuestChat = (): { openChat: ((moveId: number, consumerPickup?: boolean) => void) | null } => {
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks -- always called; the try only catches the missing-provider throw
    const { openChat } = useChatModal();
    return { openChat };
  } catch {
    return { openChat: null };
  }
};

/**
 * The subject move for the action dialogs, fetched only when one opens.
 *
 * Every one of the embed's dialogs takes a whole `Moves` row and resolves its pair and its offers
 * itself. None of that is on the Orders tab's page query, and putting it there would make every
 * card carry relation trees for actions almost nobody clicks — so the move-details query, which
 * those dialogs were built against, runs once on open.
 */
const useMoveForActions = (moveId: number | null) => {
  const { data } = useGetMoveDetailsQuery({
    variables: { moveId: moveId ?? 0 },
    skip: moveId === null,
    fetchPolicy: 'cache-and-network',
  });

  const move = (data?.moves_by_pk ?? null) as Moves | null;
  const offers = useMemo(() => (findOffersInScenarios(move?.scenariotomoves ?? []) ?? []) as Offers[], [move]);

  return { move, offers };
};

export const EmbedOrderInteraction: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useUser();
  const { openChat } = useEmbedGuestChat();

  const [photoTarget, setPhotoTarget] = useState<ScenarioLegPhotoTarget | null>(null);
  const [dialogTarget, setDialogTarget] = useState<EmbedDialogTarget | null>(null);

  const { move: dialogMove, offers: dialogOffers } = useMoveForActions(dialogTarget?.moveId ?? null);
  const closeDialog = () => setDialogTarget(null);

  const { assigningScenarioIds, beginSettling } = useOrderAssignmentSettling();

  // The assign modal's close IS the assignment event: an output means its mutation succeeded, and
  // the order it names holds an assigning pill until the data agrees.
  const handleAssignModalClose = (output?: { action?: ScenarioAssignmentAction; driverId?: number | null }) => {
    const target = dialogTarget;
    closeDialog();
    if (!target || !output?.action) return;
    beginSettling({ scenarioId: target.scenarioId, action: output.action, driverId: output.driverId ?? null });
  };

  // The embed's configuration rides on the query string, so every in-app navigation carries it
  // forward or the destination loses the integrator's settings.
  const search = location.search;

  const value = useMemo<OrderInteraction>(() => {
    const openDialog = (view: ScenarioView, kind: EmbedDialogKind) => {
      const subjectMoveId = view.actions.openTargets[0];
      if (subjectMoveId !== undefined) setDialogTarget({ kind, moveId: subjectMoveId, scenarioId: view.scenarioId });
    };

    // The planner takes the order's drive legs directly; `MovePlannerProvider` reads exactly
    // these router-state keys. `LegacyRedirect` carries no state, so this targets `/moves` itself.
    const openPlanner = (view: ScenarioView, inverse: boolean) => {
      const [moveId1, moveId2] = view.actions.openTargets;
      navigate(appendSearch(planMovesPath(), search), {
        state: { moveId1, inverse, ...(moveId2 && moveId2 !== moveId1 ? { moveId2 } : {}) },
      });
    };

    const buildCardActions = (view: ScenarioView): ValetOrderHandlers => ({
      onAssignDriver: () => openDialog(view, 'assign'),
      onOpenMove: (moveId: number) => navigate(appendSearch(movePath(moveId), search)),
      onOpenChat: () => {
        if (view.actions.chatMoveId !== null) openChat?.(view.actions.chatMoveId);
      },
      onCreateDuplicate: () => openPlanner(view, false),
      onCreateInverse: () => openPlanner(view, true),
      onChangePromiseTime: () => openDialog(view, 'changeTime'),
      onCancelOrder: () => openDialog(view, 'cancel'),
    });

    return {
      resolveCardActions: (model, view) =>
        model.product === 'moves' ? toOrderCardActions(buildCardActions(view as ScenarioView)) : undefined,
      isAssigning: model => model.product === 'moves' && assigningScenarioIds.has(model.id),
      onOpenPhotos: (itemId, phase) => setPhotoTarget({ moveId: itemId, phase }),
    };
  }, [navigate, search, openChat, assigningScenarioIds]);

  // The cancel flow differs by tier, exactly as the embed's move details routes it: SaaS cancels
  // outright, Full Service raises a request for dispatcher review.
  const isCancelOpen = dialogTarget?.kind === 'cancel' && !!dialogMove;
  const isLiteMove = dialogMove?.lite === true;

  return (
    <EmbedOrderInteractionContext.Provider value={value}>
      {children}

      <ScenarioPhotoLightboxByLegPhase target={photoTarget} onClose={() => setPhotoTarget(null)} />

      <AssignOrUnassignMoveModal
        open={dialogTarget?.kind === 'assign' && !!dialogMove}
        onClose={handleAssignModalClose}
        moveInput={dialogMove}
        offersInput={dialogOffers}
      />

      <MoveTimeModal
        open={dialogTarget?.kind === 'changeTime' && !!dialogMove}
        onClose={closeDialog}
        moveInput={dialogMove ?? undefined}
        actor={actorIdentity(user)}
      />

      {isLiteMove ? (
        <CancelLiteModal
          open={isCancelOpen}
          onClose={closeDialog}
          moveInput={dialogMove}
          actor={actorIdentity(user)}
          onCreateDuplicate={state => navigate(appendSearch(planMovesPath(), search), { state })}
          onError={err => Sentry.captureException(err)}
        />
      ) : (
        <MoveCancelModal
          open={isCancelOpen}
          onClose={closeDialog}
          moveInput={dialogMove}
          adminUrl={siteConfig.hopdrive.admin.url}
          getAuthToken={async () => user?.getToken()}
          onError={err => Sentry.captureException(err)}
        />
      )}
    </EmbedOrderInteractionContext.Provider>
  );
};

export default EmbedOrderInteraction;
